import type { Colormap } from './om/om-colormap.js'

export interface OmModelDef {
  id: string
  label: string
  resolution: string
  coverage: string
  horizonLabel: string
  horizonHours: number
  variables: Set<string>
  /** Bounding box [latMin, lonMin, latMax, lonMax] — null = global */
  bbox: [number, number, number, number] | null
  /** Higher = preferred when multiple models cover a point */
  priority: number
  /** Optional higher-resolution dataset to switch to at/above `minZoom` (e.g.
   *  the IFS layer uses ifs025 zoomed out, the 9km reduced-Gaussian zoomed in). */
  hiRes?: { modelId: string; minZoom: number; gaussian?: boolean }
}

export type OmVariableId =
  | 'cloud_cover'
  | 'cloud_cover_high'
  | 'cloud_cover_mid'
  | 'cloud_cover_low'
  | 'lightning_potential'
  | 'precipitation'
  | 'cape'
  | 'visibility'
  | 'wind_speed_10m'
  | 'wind_gusts_10m'
  | 'temperature_2m'
  | 'mass_density_8m'
  | 'freezing_level_height'
  | 'snowfall_height'
  | 'weather_code'

export interface OmLayerDef {
  id: string
  label: string
  variable: OmVariableId
  modelFilter: (model: OmModelDef) => boolean
  /** Optional colormap override; defaults to colormapForVariable(variable). */
  colormap?: Colormap
  /** Optional CPU remap of each scaled value before rendering (e.g. onto the shared
   *  unified lightning index). Return NaN to render a cell transparent. */
  valueTransform?: (raw: number) => number
  /** Optional NaN-aware box-blur radius (in grid cells) applied AFTER valueTransform.
   *  Use ONLY for coarse categorical fields (e.g. WMO weather_code) where spreading
   *  a value into neighbours is a legitimate smoothing of the missing spatial
   *  gradient — NOT for continuous fields (LPI/HRRR), where it would destroy real
   *  detail. NaN cells stay NaN (no data is invented). 0 = no blur. */
  smoothRadius?: number
}

// Variable sets derived from actual OM S3 data_spatial latest.json responses.
// "wind_speed_10m" is computed by the OM library from wind_u/v_component_10m.

const IFS_VARS = new Set([
  'cloud_cover',
  'cloud_cover_high',
  'cloud_cover_mid',
  'cloud_cover_low',
  'precipitation',
  'cape',
  'visibility',
  'wind_speed_10m',
  'wind_gusts_10m',
  'temperature_2m',
])

const ICON_GLOBAL_VARS = new Set([
  'cloud_cover',
  'cloud_cover_high',
  'cloud_cover_mid',
  'cloud_cover_low',
  'precipitation',
  'cape',
  'freezing_level_height',
  'wind_speed_10m',
  'wind_gusts_10m',
  'temperature_2m',
  'weather_code',
])

const ICON_EU_VARS = new Set([...ICON_GLOBAL_VARS, 'visibility', 'snowfall_height'])

const ICON_D2_VARS = new Set([...ICON_EU_VARS, 'lightning_potential'])

const HRRR_VARS = new Set([
  'cloud_cover',
  'cloud_cover_high',
  'cloud_cover_mid',
  'cloud_cover_low',
  'precipitation',
  'cape',
  'visibility',
  'freezing_level_height',
  'wind_speed_10m',
  'wind_gusts_10m',
  'temperature_2m',
  'mass_density_8m',
])

const GFS_SURFACE_VARS = new Set([
  'cloud_cover',
  'precipitation',
  'temperature_2m',
  'wind_speed_10m',
])

const AROME_HD_VARS = new Set([
  'cloud_cover_high',
  'cloud_cover_mid',
  'cloud_cover_low',
  'precipitation',
  'cape',
  'wind_speed_10m',
  'wind_gusts_10m',
  'temperature_2m',
])

const AROME_VARS = new Set([
  'cloud_cover',
  'cloud_cover_high',
  'cloud_cover_mid',
  'cloud_cover_low',
  'precipitation',
  'cape',
  'wind_speed_10m',
  'wind_gusts_10m',
  'temperature_2m',
])

const NORDIC_VARS = new Set(['cloud_cover', 'precipitation', 'wind_gusts_10m', 'temperature_2m'])

const UKMO_VARS = new Set([
  'cloud_cover',
  'cloud_cover_high',
  'cloud_cover_mid',
  'cloud_cover_low',
  'precipitation',
  'cape',
  'visibility',
  'freezing_level_height',
  'wind_gusts_10m',
  'temperature_2m',
])

const GEM_HRDPS_VARS = new Set([
  'cloud_cover',
  'precipitation',
  'cape',
  'wind_gusts_10m',
  'temperature_2m',
])

const JMA_VARS = new Set([
  'cloud_cover',
  'cloud_cover_high',
  'cloud_cover_mid',
  'cloud_cover_low',
  'precipitation',
  'temperature_2m',
  'wind_speed_10m',
])

export const OM_MODELS: OmModelDef[] = [
  {
    // Dual-resolution ECMWF IFS: 0.25° regular grid zoomed out (standard GPU
    // path), switching to the 9km reduced-Gaussian `ecmwf_ifs` (O1280) zoomed in
    // — see buildOmDataUrl / readViewportFieldGaussian.
    id: 'ecmwf_ifs025',
    label: 'ECMWF IFS',
    resolution: '0.25°/9km',
    coverage: 'Global',
    horizonLabel: '6d',
    horizonHours: 144,
    variables: IFS_VARS,
    bbox: null,
    priority: 3,
    hiRes: { modelId: 'ecmwf_ifs', minZoom: 5, gaussian: true },
  },
  {
    id: 'dwd_icon',
    label: 'ICON Global',
    resolution: '13km',
    coverage: 'Global',
    horizonLabel: '7.5d',
    horizonHours: 180,
    variables: ICON_GLOBAL_VARS,
    bbox: null,
    priority: 2,
  },
  {
    id: 'ncep_gfs013',
    label: 'GFS',
    resolution: '13km',
    coverage: 'Global',
    horizonLabel: '16d',
    horizonHours: 384,
    variables: GFS_SURFACE_VARS,
    bbox: null,
    priority: 1,
  },
  {
    id: 'dwd_icon_eu',
    label: 'ICON-EU',
    resolution: '7km',
    coverage: 'Europe',
    horizonLabel: '5d',
    horizonHours: 120,
    variables: ICON_EU_VARS,
    // True grid extent from crs_wkt BBOX (was under-claiming lonMax 45° vs 62.5°).
    bbox: [29.5, -23.5, 70.5, 62.5],
    priority: 5,
  },
  {
    id: 'dwd_icon_d2',
    label: 'ICON-D2',
    resolution: '2km',
    coverage: 'Central EU',
    horizonLabel: '48h',
    horizonHours: 48,
    variables: ICON_D2_VARS,
    bbox: [43.18, -3.94, 58.1, 20.36],
    priority: 10,
  },
  {
    id: 'ncep_hrrr_conus',
    label: 'HRRR',
    resolution: '3km',
    coverage: 'CONUS',
    horizonLabel: '48h',
    horizonHours: 48,
    variables: HRRR_VARS,
    // TRUE geographic bounding box of the LCC fan, not the SW/NE corner points:
    // the fan top bulges to ~52.6°N at the central meridian and the NW edge
    // reaches ~-134°. The old corner-based bbox (…,47.84,…) cut off ~5° of the
    // northern US, so auto-select abandoned HRRR there and fell back to a coarser
    // (or mis-projected) model. Computed by scanning the projected grid extent.
    bbox: [21.14, -134.1, 52.62, -60.92],
    priority: 10,
  },
  {
    id: 'meteofrance_arome_france_hd',
    label: 'AROME HD',
    resolution: '1.5km',
    coverage: 'France',
    horizonLabel: '42h',
    horizonHours: 42,
    variables: AROME_HD_VARS,
    bbox: [37.5, -12.0, 55.4, 16.0],
    priority: 12,
  },
  {
    id: 'meteofrance_arome_france0025',
    label: 'AROME',
    resolution: '2.5km',
    coverage: 'France',
    horizonLabel: '42h',
    horizonHours: 42,
    variables: AROME_VARS,
    bbox: [37.5, -12.0, 55.4, 16.0],
    priority: 11,
  },
  {
    id: 'metno_nordic_pp',
    label: 'MET Nordic',
    resolution: '1km',
    coverage: 'Scandinavia',
    horizonLabel: '2.5d',
    horizonHours: 60,
    variables: NORDIC_VARS,
    // True geo extent of the LCC fan (computed by scanning the projected grid).
    bbox: [52.3, -11.76, 73.86, 41.76],
    priority: 12,
  },
  {
    id: 'ukmo_uk_deterministic_2km',
    label: 'UKMO',
    resolution: '2km',
    coverage: 'UK',
    horizonLabel: '5d',
    horizonHours: 120,
    variables: UKMO_VARS,
    // True geo extent of the LAEA grid (computed by scanning the projected grid).
    bbox: [44.51, -24.6, 63.02, 15.35],
    priority: 10,
  },
  {
    id: 'cmc_gem_hrdps',
    label: 'GEM HRDPS',
    resolution: '2.5km',
    coverage: 'Canada',
    horizonLabel: '48h',
    horizonHours: 48,
    variables: GEM_HRDPS_VARS,
    // True geo envelope of the rotated-pole grid (computed from the rotation;
    // see om-rotated / buildRotatedProjection). Rotated N pole at 36.09°N,65.31°E.
    bbox: [27.28, -152.73, 70.61, -40.71],
    priority: 10,
  },
  {
    id: 'jma_msm',
    label: 'JMA MSM',
    resolution: '5km',
    coverage: 'Japan',
    horizonLabel: '39h',
    horizonHours: 39,
    variables: JMA_VARS,
    // True grid extent from crs_wkt BBOX (was under-claiming the edges).
    bbox: [22.4, 120.0, 47.6, 150.0],
    priority: 10,
  },
]

export const OM_MODELS_BY_ID = new Map(OM_MODELS.map(m => [m.id, m]))

export const OM_LAYERS: OmLayerDef[] = [
  {
    id: 'om-cloud-cover',
    label: 'Cloud Cover',
    variable: 'cloud_cover',
    modelFilter: m => m.variables.has('cloud_cover'),
  },
  {
    id: 'om-cloud-cover-high',
    label: 'Cloud Cover High',
    variable: 'cloud_cover_high',
    modelFilter: m => m.variables.has('cloud_cover_high'),
  },
  {
    id: 'om-cloud-cover-mid',
    label: 'Cloud Cover Mid',
    variable: 'cloud_cover_mid',
    modelFilter: m => m.variables.has('cloud_cover_mid'),
  },
  {
    id: 'om-cloud-cover-low',
    label: 'Cloud Cover Low',
    variable: 'cloud_cover_low',
    modelFilter: m => m.variables.has('cloud_cover_low'),
  },
  {
    id: 'om-precipitation',
    label: 'Precipitation',
    variable: 'precipitation',
    modelFilter: m => m.variables.has('precipitation'),
  },
  {
    id: 'om-cape',
    label: 'CAPE',
    variable: 'cape',
    modelFilter: m => m.variables.has('cape'),
  },
  {
    id: 'om-visibility',
    label: 'Visibility',
    variable: 'visibility',
    modelFilter: m => m.variables.has('visibility'),
  },
  {
    // Animated particle flow (OmParticleLayer): direction + sustained speed from
    // wind_u/v_component_10m, with gusts (wind_gusts_10m) shown as a boldness +
    // speed surge. Folds in the former standalone gust layer. The
    // `wind_speed_10m` proxy reliably selects the u/v-capable models (regional
    // speed-only models like MET Nordic/UKMO/GEM lack u/v → fall back to a global
    // u/v model that also carries gusts).
    id: 'om-wind',
    label: 'Wind',
    variable: 'wind_speed_10m',
    modelFilter: m => m.variables.has('wind_speed_10m'),
  },
  {
    id: 'om-temperature',
    label: 'Temperature',
    variable: 'temperature_2m',
    modelFilter: m => m.variables.has('temperature_2m'),
  },
  {
    id: 'om-smoke',
    label: 'Smoke (HRRR)',
    variable: 'mass_density_8m',
    modelFilter: m => m.variables.has('mass_density_8m'),
  },
  {
    id: 'om-freezing-level',
    label: 'Freezing Level',
    variable: 'freezing_level_height',
    modelFilter: m => m.variables.has('freezing_level_height'),
  },
  {
    id: 'om-snow-line',
    label: 'Snow Line',
    variable: 'snowfall_height',
    modelFilter: m => m.variables.has('snowfall_height'),
  },
]

export const OM_LAYERS_BY_ID = new Map(OM_LAYERS.map(l => [l.id, l]))

// `latest.json` is the only MUTABLE file: it's fetched DIRECTLY from Open-Meteo's
// Cloudflare-fronted CDN, which serves it fresh (cf-cache-status: DYNAMIC) so the
// app always tracks the newest model run — no stale-cache problem. It's CORS-
// enabled (Access-Control-Allow-Origin: *). CSP connect-src must allow
// map-tiles.open-meteo.com.
export const OM_LATEST_BASE_URL = 'https://map-tiles.open-meteo.com/data_spatial'

// `.om` tiles are IMMUTABLE (a given run/valid-time file never changes), so they
// go through OUR CloudFront — same-origin `/om/*`, origin = Open-Meteo's S3 bucket
// (us-west-2) — for hard edge caching and consistent range-request latency (avoids
// Cloudflare's full-file fetch on a cache MISS). In dev, the vite `/om` proxy
// forwards to the same S3 bucket. See the `/om/*` behavior in web-hosting-stack.ts
// and cloudfront-functions/om-rewrite.js.
export const OM_TILE_BASE_URL = '/om/data_spatial'
